import { isValidHex, normalizeHex } from "@/lib/components/ui/color-field/color-field.utils";
import type { TFunction } from "i18next";
import { z } from "zod";
import type { FronteggGroup } from "./groups.utils";

export const GROUPS_COLOR_CHOICES = [
  "#EC4899",
  "#EF4444",
  "#F97316",
  "#EAB308",
  "#22C55E",
  "#3B82F6",
  "#8B5CF6",
] as const;

export const DEFAULT_GROUP_COLOR = GROUPS_COLOR_CHOICES[5];

export type GroupFormValues = {
  name: string;
  color: string;
  roleIds: string[];
  userIds: string[];
  description: string;
};

export function createGroupFormSchema(t: TFunction) {
  return z.object({
    name: z.string().refine((value) => value.trim().length > 0, {
      message: t("settings_groups_form_name_required"),
    }),
    color: z.string().refine((value) => isValidHex(value), {
      message: t("settings_groups_form_color_invalid"),
    }),
    roleIds: z.array(z.string()),
    userIds: z.array(z.string()),
    description: z.string(),
  });
}

export function getGroupFormDefaults(
  group?: FronteggGroup | null,
): GroupFormValues {
  const rawColor = typeof group?.color === "string" ? group.color : "";
  const color =
    normalizeHex(rawColor) ??
    (isValidHex(rawColor) ? rawColor : DEFAULT_GROUP_COLOR);

  return {
    name: group?.name ?? "",
    color,
    roleIds: Array.isArray(group?.roles)
      ? group.roles.map((role) => role.id)
      : [],
    userIds: Array.isArray(group?.users)
      ? group.users.map((user) => user.id)
      : [],
    description:
      typeof group?.description === "string" ? group.description : "",
  };
}

export function mergeGroupColorChoices(current?: string | null): string[] {
  const normalized = current ? normalizeHex(current) : null;
  if (!normalized) {
    return [...GROUPS_COLOR_CHOICES];
  }
  if (
    GROUPS_COLOR_CHOICES.some(
      (hex) => hex.toUpperCase() === normalized.toUpperCase(),
    )
  ) {
    return [...GROUPS_COLOR_CHOICES];
  }
  return [normalized, ...GROUPS_COLOR_CHOICES];
}
